import React from "react";
import ITransactionHistoryTableRow from "./ITransactionHistoryTableRow";
import CurrencyElement from "@appComponents/CurrencyElement/CurrencyElement";
import ZebraDetailsCard from "@appComponents/ZebraDetailsCard/ZebraDetailsCard";
import { formatDate } from "@features/utils/functions";
import { classNames } from "@features/utils/reactFunctions";
import ITransaction, {
  TransactionTypes,
  TransactionTypesDescriptions,
} from "@funds/interfaces/ITransaction";
import { MAIN_CURRENCY_LABEL } from "@bundles/consts";

import * as elements from "@appStyles/_elements.module.scss";
import * as style from "./TransactionHistoryTableRowStyle.module.scss"

export const labels = {
  associated_records: "Related transaction",
  company: "Related Company",
  spv: "Related SPV",
  pitch: "Related Pitch",
  crypto_trade: "Related Crypto Trade",
  id: "Id",
  created_at: "Date",
  fee_amount: "Fee",
  exchange_rate: "Exchange Rate",
  income_amount: "Income Amount",
  outcome_amount: "Outcome Amount",
  income_currency: "Income Currency",
  outcome_currency: "Outcome Currency",
  amount: "Amount",
  currency: "Currency",
  type: "Description",
  name: "Name",
  title: "Title",
  tx_id: "Transaction ID",
};

const TransactionHistoryTableRow: React.FunctionComponent<ITransactionHistoryTableRow> =
  React.memo((props) => {
    const { transaction, balance } = props;
    const [expandedState, setExpandedState] = React.useState(false);

    const hasBalance = balance !== null;
    const isPositive = Boolean(transaction.in);

    const renderCell = (
      element: JSX.Element | string,
      label: string,
      classIndex: number
    ) => {
      const hideOnMobile = (label: string) => {
        if (label !== `Income: ` && label !== `Outcome: `) {
          return false;
        }

        return label === `Income: ` ? !isPositive : isPositive;
      };

      return (
        <div
          data-title={label}
          data-hide={hideOnMobile(label)}
          className={classNames(
            elements.zebraTableItem,
            style.zebraTableItem,
            addedClass[classIndex],
            hasBalance && withBalanceClass[classIndex]
          )}
        >
          {element}
        </div>
      );
    };

    const renderAmount = (value: number | string): JSX.Element | string => {
      return value !== ` ` ? (
        <span data-sign={`\xa0${Boolean(transaction.in) ? `+` : `-`}`}>
          <CurrencyElement value={value} currency={transaction.currency} />
        </span>
      ) : (
        ` - `
      );
    };

    const formatValue = (
      label: string,
      value: keyof typeof TransactionTypesDescriptions | string | number,
      parent: string,
      currency?: string | false
    ) => {
      switch (label) {
        case "Income Amount":
          return transaction.details.crypto_trade?.income_currency ? (
            <CurrencyElement
              withCurrencyLabel={false}
              currency={transaction.details.crypto_trade?.income_currency}
              value={value}
            />
          ) : (
            value
          );
        case "Outcome Amount":
          return transaction.details.crypto_trade?.outcome_currency ? (
            <CurrencyElement
              withCurrencyLabel={false}
              currency={transaction.details.crypto_trade?.outcome_currency}
              value={value}
            />
          ) : (
            value
          );
        case "Fee":
          return transaction.details.crypto_trade?.outcome_currency ? (
            <CurrencyElement
              currency={transaction.details.crypto_trade?.outcome_currency}
              value={value}
            />
          ) : (
            value
          );
        case "Exchange Rate":
          return transaction.details.crypto_trade?.exchange_rate ? (
            <CurrencyElement
              currency={transaction.details.crypto_trade?.exchange_rate}
              value={value}
              withCurrencyLabel={false}
            />
          ) : (
            value
          );
        case "Amount":
          return currency ? (
            <CurrencyElement currency={currency} value={value} />
          ) : (
            value
          );
        case "Currency":
        case "Income Currency":
        case "Outcome Currency":
          return value === "bfusd"
            ? MAIN_CURRENCY_LABEL.toUpperCase()
            : value.toString().toUpperCase();
        case "Date":
          return typeof value === "string" && formatDate(value);
        case "Name":
        case "Title":
          switch (parent) {
            case "Related SPV":
              return (
                <a
                  href={`/companies/${transaction.details?.company?.slug}/market/spvs/${transaction.details?.spv?.id}/orders`}
                >
                  {value}
                </a>
              );
            case "Related Company":
              return (
                <a href={`/companies/${transaction.details?.company?.slug}`}>
                  {value}
                </a>
              );
            case "Related Pitch":
              return (
                <a href={`/pitches/${transaction.details?.pitch?.slug}`}>
                  {value}
                </a>
              );
            default:
              return value;
          }
        case "Description":
          return (
            transactionDescription(transaction, value)
        );
        case "Transaction ID":
          return transaction.details.tx_url ? (
            <a
              href={transaction.details.tx_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {value}
            </a>
          ) : (
            value
          );
        default:
          return value;
      }
    };

    const iconClass = expandedState ? "fa fa-angle-down" : "fa fa-angle-right";

    const addedClass = [
      style.zebraTableItemDate,
      style.zebraTableItemDescription,
      style.zebraTableItemIn,
      style.zebraTableItemOut,
      style.balanceBalance,
    ];

    const withBalanceClass = [
      style.balanceDate,
      style.balanceDescription,
      style.balanceIn,
      style.balanceOut,
      "",
    ];

    const transactionDescription = (transaction: any, value: any) => {
      if (transaction.type === "PublicEquityShares::Fee") {
        value = `${transaction.type}::${transaction.details?.additional_information?.subtype}`;
      }

      let text = transaction.details.description || TransactionTypesDescriptions[value];

      if (transaction.type == 'PublicEquityShares::Deposit') {
        let companyName = transaction.details.company?.name || transaction.details?.additional_information?.company;
        text = text.replace("<company>", companyName);
      }

      return text;
    }

    const mapTransactionTypes = (transaction: ITransaction) => {
      const isStaking = transaction.currency === "staked_eth";
      const isEth = transaction.currency === "eth";

      if (isStaking) {
        if (transaction.type === "EthStakingDeposit")
          return "Staked ETH Staking Deposit";
        if (transaction.type === "EthStakingTransfer")
          return "Staked ETH Unstaking";
      }

      if (isEth) {
        if (transaction.type === "EthStakingDeposit")
          return "ETH Staking Deposit";
        if (transaction.type === "EthStakingTransfer")
          return "ETH Staking Transfer";
      }

      if (transaction.type === "PublicEquityShares::Fee") {
        return TransactionTypes[`${transaction.type}::${transaction.details?.additional_information?.subtype}`];
      }

      return TransactionTypes[transaction.type];
    };

    return (
      <div
        key={transaction.transaction_id}
        className={classNames(elements.zebraTableRow, style.zebraTableRow)}
        onClick={() => setExpandedState((prevState) => !prevState)}
      >
        {renderCell(formatDate(transaction.created_at), `Date: `, 0)}
        {renderCell(mapTransactionTypes(transaction), `Description: `, 1)}
        {renderCell(
          renderAmount(
            Number(transaction.in) > 0 ? Number(transaction.in) : ` `
          ),
          `Income: `,
          2
        )}
        {renderCell(
          renderAmount(
            Number(transaction.out) > 0 ? Number(transaction.out) : ` `
          ),
          `Outcome: `,
          3
        )}
        {hasBalance &&
          renderCell(
            <CurrencyElement
              value={transaction.balance || 0}
              currency={transaction.currency}
            />,
            `Balance:\xa0`,
            4
          )}
        <div
          className={[elements.arrowIcon, iconClass, style.dragRight].join(" ")}
        />
        {expandedState ? (
          <ZebraDetailsCard
            data={{
              type: transaction.type,
              id: transaction.transaction_id,
              ...transaction.details,
            }}
            labels={labels}
            formatValue={formatValue}
          />
        ) : null}
      </div>
    );
  });

export default TransactionHistoryTableRow;
